import React, { useState, useEffect, useRef } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import { 
  FaBreadSlice, 
  FaLayerGroup, 
  FaList, 
  FaSearch, 
  FaSyncAlt, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaTimes, 
  FaSave,
  FaBoxOpen,
  FaWeight,
  FaUtensils,
  FaChevronDown,
  FaChevronRight,
  FaCopy,
  FaArrowRight,
  FaFileExcel,
  FaFileImport
} from 'react-icons/fa';
import './admin/Admin.css';

const QuanLyDinhMuc = () => {
  const [dinhMucList, setDinhMucList] = useState([]);
  const [sanPhamList, setSanPhamList] = useState([]);
  const [nguyenLieuList, setNguyenLieuList] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    MaSP: '',
    TenUser: 'admin',
    ingredients: [{ MaNL: '', DinhMuc: '', DinhMuc_QuyDoi: '' }]
  });
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [showInGrams, setShowInGrams] = useState(false);
  const [showCopySection, setShowCopySection] = useState(false);
  const [sourceProduct, setSourceProduct] = useState('');
  const [sourceQuotas, setSourceQuotas] = useState([]);
  const [loadingSourceQuotas, setLoadingSourceQuotas] = useState(false);
  const [isImportingExcel, setIsImportingExcel] = useState(false);
  const fileInputRef = useRef(null);

  // Utility function to round to 5 decimal places
  const roundToFiveDecimals = (num) => {
    return Math.round((parseFloat(num) + Number.EPSILON) * 100000) / 100000;
  };

  const normalizeExcelHeader = (value) => {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  };

  const getExcelCellValue = (row, possibleHeaders) => {
    const normalizedHeaders = possibleHeaders.map(normalizeExcelHeader);
    const matchedKey = Object.keys(row || {}).find((key) =>
      normalizedHeaders.includes(normalizeExcelHeader(key))
    );

    return matchedKey ? row[matchedKey] : undefined;
  };

  const parseExcelNumber = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const rawValue = String(value).trim();
    if (!rawValue) {
      return null;
    }

    const hasComma = rawValue.includes(',');
    const hasDot = rawValue.includes('.');

    let normalizedValue = rawValue.replace(/\s/g, '');

    if (hasComma && hasDot) {
      normalizedValue = normalizedValue.replace(/,/g, '');
    } else if (hasComma) {
      normalizedValue = normalizedValue.replace(',', '.');
    }

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  };

  const formatImportedQuotaRow = (row) => {
    const maSP = String(getExcelCellValue(row, ['MaSP', 'Mã sản phẩm', 'Ma san pham']) || '').trim();
    const maNL = String(getExcelCellValue(row, ['MaNL', 'Mã nguyên liệu', 'Ma nguyen lieu']) || '').trim();
    const dinhMuc = parseExcelNumber(getExcelCellValue(row, ['DinhMuc', 'Định mức', 'Dinh muc']));
    const dinhMucQuyDoi = parseExcelNumber(
      getExcelCellValue(row, ['DinhMuc_QuyDoi', 'DinhMucQuyDoi', 'Định mức quy đổi', 'Dinh muc quy doi'])
    );
    const tenUser = String(
      getExcelCellValue(row, ['TenUser', 'Tên user', 'Tên người tạo', 'Nguoi tao']) || 'admin'
    ).trim();

    return {
      MaSP: maSP,
      MaNL: maNL,
      DinhMuc: dinhMuc,
      DinhMuc_QuyDoi: dinhMucQuyDoi ?? 0,
      TenUser: tenUser || 'admin'
    };
  };

  const fetchAllQuotaRecords = async () => {
    const response = await fetch('https://doticom.vn:3000/api/dinh-muc-nguyen-lieu');
    if (!response.ok) {
      throw new Error('Không thể tải dữ liệu định mức hiện tại');
    }
    return response.json();
  };

  // Get filtered quota list based on search and selected product
  const getFilteredDinhMuc = () => {
    let filtered = dinhMucList;

    // Filter by selected product
    if (selectedProduct && selectedProduct !== 'all') {
      filtered = filtered.filter(item => item.MaSP === selectedProduct);
    }

    // Filter by search keyword
    if (searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter(item => 
        item.MaSP?.toLowerCase().includes(keyword) ||
        item.MaNL?.toLowerCase().includes(keyword) ||
        item.TenSP?.toLowerCase().includes(keyword) ||
        item.TenNL?.toLowerCase().includes(keyword)
      );
    }

    return filtered;
  };

  // Get statistics
  const getStats = () => {
    const totalQuotas = dinhMucList.length;
    const totalProducts = sanPhamList.length;
    const filteredQuotas = getFilteredDinhMuc().length;
    
    return { totalQuotas, totalProducts, filteredQuotas };
  };

  // Check if a product has quotas
  const getProductsWithQuotas = () => {
    const productsWithQuotas = new Set();
    dinhMucList.forEach(item => {
      productsWithQuotas.add(item.MaSP);
    });
    return productsWithQuotas;
  };

  // Enrich quota data with product and ingredient details
  const enrichDinhMucData = (dinhMucData) => {
    return dinhMucData.map(item => {
      // Find product details
      const product = sanPhamList.find(p => p.MaSP === item.MaSP);
      // Find ingredient details - fix the mapping here
      const ingredient = nguyenLieuList.find(nl => nl.MaNL === item.MaNL);
      
      return {
        ...item,
        DinhMuc: roundToFiveDecimals(item.DinhMuc), // Round DinhMuc to 5 decimal places
        TenSP: product?.Ten_SP || null,
        TenNL: ingredient?.TenNL || null,
        DVT: ingredient?.DVT || null
      };
    });
  };

  // Fetch quotas for a specific product (for copying)
  const fetchQuotasForCopy = async (maSP) => {
    try {
      setLoadingSourceQuotas(true);
      const response = await fetch(`https://doticom.vn:3000/api/dinh-muc-nguyen-lieu/theo-san-pham/${maSP}`);
      if (response.ok) {
        const data = await response.json();
        const enrichedData = enrichDinhMucData(data);
        setSourceQuotas(enrichedData);
        return enrichedData;
      } else {
        console.error('Failed to fetch source quotas');
        toast.error('Không thể tải định mức từ sản phẩm nguồn', {
          position: "top-right",
          autoClose: 3000,
        });
        return [];
      }
    } catch (error) {
      console.error('Error fetching source quotas:', error);
      toast.error('Có lỗi xảy ra khi tải định mức nguồn', {
        position: "top-right",
        autoClose: 3000,
      });
      return [];
    } finally {
      setLoadingSourceQuotas(false);
    }
  };

  // Handle source product change for copying
  const handleSourceProductChange = async (e) => {
    const selectedMaSP = e.target.value;
    setSourceProduct(selectedMaSP);
    
    if (selectedMaSP) {
      await fetchQuotasForCopy(selectedMaSP);
    } else {
      setSourceQuotas([]);
    }
  };

  // Copy quotas from source product
  const copyQuotasFromSource = () => {
    if (sourceQuotas.length === 0) {
      toast.warning('Sản phẩm nguồn không có định mức để sao chép', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Convert source quotas to ingredients format
    const copiedIngredients = sourceQuotas.map(quota => ({
      MaNL: quota.MaNL,
      DinhMuc: quota.DinhMuc.toString(),
      DinhMuc_QuyDoi: (quota.DinhMuc_QuyDoi || '').toString()
    }));

    // Merge with existing ingredients, avoiding duplicates
    const existingMaNLs = new Set(formData.ingredients.map(ing => ing.MaNL));
    const newIngredients = copiedIngredients.filter(ing => !existingMaNLs.has(ing.MaNL));
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      ingredients: [
        ...prev.ingredients.filter(ing => ing.MaNL && ing.DinhMuc), // Keep existing valid ingredients
        ...newIngredients, // Add new ingredients from source
        { MaNL: '', DinhMuc: '', DinhMuc_QuyDoi: '' } // Add empty row for new additions
      ]
    }));

    // Show success message
    toast.success(`Đã sao chép ${newIngredients.length} định mức từ ${sourceProduct}`, {
      position: "top-right",
      autoClose: 3000,
    });

    // Hide copy section
    setShowCopySection(false);
    setSourceProduct('');
    setSourceQuotas([]);
  };

  // Get products that have quotas (for copy source selection)
  const getProductsWithQuotasForCopy = () => {
    const productsWithQuotas = new Set();
    dinhMucList.forEach(item => {
      productsWithQuotas.add(item.MaSP);
    });
    
    return sanPhamList.filter(product => 
      productsWithQuotas.has(product.MaSP) && 
      product.MaSP !== formData.MaSP // Exclude current product
    );
  };







  // Fetch all quota records with related data
  const fetchDinhMuc = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://doticom.vn:3000/api/dinh-muc-nguyen-lieu');
      if (response.ok) {
        const data = await response.json();
        // Enrich data with product and ingredient details
        const enrichedData = enrichDinhMucData(data);
        setDinhMucList(enrichedData);
      } else {
        console.error('Failed to fetch quotas');
        toast.error('Không thể tải danh sách định mức', {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error('Error fetching quotas:', error);
      toast.error('Có lỗi xảy ra khi tải dữ liệu', {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const exportDinhMucToExcel = () => {
    const exportRows = filteredDinhMuc.map((item) => ({
      MaSP: item.MaSP || '',
      TenSP: item.TenSP || '',
      MaNL: item.MaNL || '',
      TenNL: item.TenNL || '',
      DinhMuc: roundToFiveDecimals(item.DinhMuc || 0),
      DinhMuc_QuyDoi: roundToFiveDecimals(item.DinhMuc_QuyDoi || 0),
      DVT: item.DVT || '',
      TenUser: item.TenUser || 'admin'
    }));

    if (exportRows.length === 0) {
      toast.warning('Không có định mức để xuất Excel', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    const templateRows = [
      {
        MaSP: 'BM001',
        TenSP: 'Bánh mì mẫu',
        MaNL: 'NL001',
        TenNL: 'Nguyên liệu mẫu',
        DinhMuc: 0.1,
        DinhMuc_QuyDoi: 100,
        DVT: 'KG',
        TenUser: 'admin'
      }
    ];

    const dataSheet = XLSX.utils.json_to_sheet(exportRows);
    const templateSheet = XLSX.utils.json_to_sheet(templateRows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, dataSheet, 'DinhMuc');
    XLSX.utils.book_append_sheet(workbook, templateSheet, 'MauImport');

    const fileName = `DinhMucNguyenLieu_${selectedProduct && selectedProduct !== 'all' ? selectedProduct : 'TatCa'}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const triggerImportFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const importDinhMucFromExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const isConfirmed = window.confirm(`Bạn có muốn import dữ liệu của file "${file.name}" không?`);
    if (!isConfirmed) {
      event.target.value = '';
      return;
    }

    try {
      setIsImportingExcel(true);
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

      if (!rawRows.length) {
        toast.warning('File Excel không có dữ liệu để import', {
          position: 'top-right',
          autoClose: 3000,
        });
        return;
      }

      const parsedRows = rawRows.map(formatImportedQuotaRow);
      const dedupedMap = new Map();
      const skippedRows = [];
      const productMap = new Map(sanPhamList.map((item) => [item.MaSP, item]));
      const ingredientMap = new Map(nguyenLieuList.map((item) => [item.MaNL, item]));

      parsedRows.forEach((row, index) => {
        const rowNumber = index + 2;

        if (!row.MaSP || !row.MaNL) {
          skippedRows.push(`Dòng ${rowNumber}: thiếu MaSP hoặc MaNL`);
          return;
        }

        if (row.DinhMuc === null) {
          skippedRows.push(`Dòng ${rowNumber}: Định mức không hợp lệ`);
          return;
        }

        if (!productMap.has(row.MaSP)) {
          skippedRows.push(`Dòng ${rowNumber}: không tìm thấy sản phẩm ${row.MaSP}`);
          return;
        }

        if (!ingredientMap.has(row.MaNL)) {
          skippedRows.push(`Dòng ${rowNumber}: không tìm thấy nguyên liệu ${row.MaNL}`);
          return;
        }

        dedupedMap.set(`${row.MaSP}__${row.MaNL}`, {
          ...row,
          DinhMuc: roundToFiveDecimals(row.DinhMuc),
          DinhMuc_QuyDoi: roundToFiveDecimals(row.DinhMuc_QuyDoi || 0)
        });
      });

      const importRows = Array.from(dedupedMap.values());

      if (!importRows.length) {
        toast.warning(skippedRows[0] || 'Không có dòng hợp lệ để import', {
          position: 'top-right',
          autoClose: 4000,
        });
        return;
      }

      const existingQuotaRecords = await fetchAllQuotaRecords();
      const existingMap = new Map(
        existingQuotaRecords.map((item) => [`${item.MaSP}__${item.MaNL}`, item])
      );

      const rowsToCreate = [];
      const rowsToUpdate = [];

      importRows.forEach((row) => {
        if (existingMap.has(`${row.MaSP}__${row.MaNL}`)) {
          rowsToUpdate.push(row);
        } else {
          rowsToCreate.push(row);
        }
      });

      let createdCount = 0;
      let updatedCount = 0;
      const failedRows = [];

      if (rowsToCreate.length > 0) {
        const createResponse = await fetch('https://doticom.vn:3000/api/dinh-muc-nguyen-lieu', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ dinhMucList: rowsToCreate })
        });

        if (createResponse.ok) {
          createdCount = rowsToCreate.length;
        } else {
          const errorData = await createResponse.json().catch(() => ({}));
          failedRows.push(`Thêm mới thất bại: ${errorData.message || 'Lỗi không xác định'}`);
        }
      }

      if (rowsToUpdate.length > 0) {
        const updateResults = await Promise.allSettled(
          rowsToUpdate.map((row) =>
            fetch(`https://doticom.vn:3000/api/dinh-muc-nguyen-lieu/${row.MaSP}/${row.MaNL}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                DinhMuc: row.DinhMuc,
                DinhMuc_QuyDoi: row.DinhMuc_QuyDoi,
                TenUser: row.TenUser
              })
            }).then(async (response) => {
              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Không thể cập nhật ${row.MaSP}/${row.MaNL}`);
              }
              return row;
            })
          )
        );

        updateResults.forEach((result) => {
          if (result.status === 'fulfilled') {
            updatedCount += 1;
          } else {
            failedRows.push(result.reason?.message || 'Có lỗi khi cập nhật định mức');
          }
        });
      }

      if (selectedProduct && selectedProduct !== 'all') {
        await fetchDinhMucByProduct(selectedProduct);
      } else {
        await fetchDinhMuc();
      }

      if (createdCount || updatedCount) {
        toast.success(`Import thành công: thêm ${createdCount}, cập nhật ${updatedCount}`, {
          position: 'top-right',
          autoClose: 4000,
        });
      }

      if (skippedRows.length || failedRows.length) {
        const messages = [...skippedRows, ...failedRows].slice(0, 5).join(' | ');
        toast.warning(messages, {
          position: 'top-right',
          autoClose: 6000,
        });
      }
    } catch (error) {
      console.error('Error importing quota excel:', error);
      toast.error(`Có lỗi xảy ra khi import Excel: ${error.message || 'Lỗi không xác định'}`, {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setIsImportingExcel(false);
      event.target.value = '';
    }
  };

  // Fetch bread products with category BMCN
  const fetchSanPham = async () => {
    try {
      const response = await fetch('https://doticom.vn:3000/items?category=BMCN2,NLBL,BMCN');
      if (response.ok) {
        const data = await response.json();
        setSanPhamList(data);
      } else {
        console.error('Failed to fetch products');
        toast.warn('Không thể tải danh sách sản phẩm', {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Có lỗi xảy ra khi tải danh sách sản phẩm', {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  // Fetch ingredients (updated API response structure)
  const fetchNguyenLieu = async () => {
    try {
      const response = await fetch('https://doticom.vn:3000/api/nguyen-lieu');
      if (response.ok) {
        const data = await response.json();
        // Map the new API structure to match expected format
        const mappedData = data.map(item => ({
          MaNL: item.MaSP,
          TenNL: item.Ten_SP,
          DVT: item.DVT_SP,
          DonGia: item.DonGia
        }));
        setNguyenLieuList(mappedData);
      } else {
        console.error('Failed to fetch ingredients');
      }
    } catch (error) {
      console.error('Error fetching ingredients:', error);
    }
  };

  // Fetch quota by product with related data
  const fetchDinhMucByProduct = async (maSP) => {
    try {
      setLoading(true);
      const response = await fetch(`https://doticom.vn:3000/api/dinh-muc-nguyen-lieu/theo-san-pham/${maSP}`);
      if (response.ok) {
        const data = await response.json();
        // Enrich data with product and ingredient details
        const enrichedData = enrichDinhMucData(data);
        setDinhMucList(enrichedData);
      } else {
        console.error('Failed to fetch quotas by product');
      }
    } catch (error) {
      console.error('Error fetching quotas by product:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add new quota with multiple ingredients
  const addDinhMuc = async (data) => {
    try {
      const dinhMucList = data.ingredients
        .filter(ing => ing.MaNL && ing.DinhMuc)
        .map(ingredient => ({
          MaSP: data.MaSP,
          MaNL: ingredient.MaNL,
          DinhMuc: roundToFiveDecimals(ingredient.DinhMuc), // Round before sending
          DinhMuc_QuyDoi: roundToFiveDecimals(ingredient.DinhMuc_QuyDoi || 0), // Add DinhMuc_QuyDoi
          TenUser: data.TenUser
        }));

      if (dinhMucList.length === 0) {
        toast.error('Vui lòng thêm ít nhất một nguyên liệu hợp lệ');
        return;
      }

      const response = await fetch('https://doticom.vn:3000/api/dinh-muc-nguyen-lieu', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ dinhMucList })
      });
      
      if (response.ok) {
        if (selectedProduct && selectedProduct !== 'all') {
          await fetchDinhMucByProduct(selectedProduct);
        } else {
          await fetchDinhMuc();
        }
        closeModal();
        toast.success(`Đã thêm thành công ${dinhMucList.length} định mức nguyên liệu`, {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Lỗi không xác định' }));
        toast.error(`Không thể thêm định mức: ${errorData.message || 'Lỗi không xác định'}`, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error('Error adding quota:', error);
      toast.error('Có lỗi xảy ra khi thêm định mức', {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  // Update quota
  const updateDinhMuc = async (maSP, maNL, data) => {
    try {
      const response = await fetch(`https://doticom.vn:3000/api/dinh-muc-nguyen-lieu/${maSP}/${maNL}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...data,
          DinhMuc: roundToFiveDecimals(data.DinhMuc), // Round before sending
          DinhMuc_QuyDoi: roundToFiveDecimals(data.DinhMuc_QuyDoi || 0) // Add DinhMuc_QuyDoi
        })
      });
      
      if (response.ok) {
        if (selectedProduct && selectedProduct !== 'all') {
          await fetchDinhMucByProduct(selectedProduct);
        } else {
          await fetchDinhMuc();
        }
        closeModal();
        toast.success('Đã cập nhật định mức nguyên liệu thành công', {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        const errorData = await response.json();
        toast.error(`Không thể cập nhật định mức: ${errorData.message || 'Lỗi không xác định'}`, {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error('Error updating quota:', error);
      toast.error('Có lỗi xảy ra khi cập nhật định mức', {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  // Delete quota
  const deleteDinhMuc = async (maSP, maNL) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa định mức này?')) {
      try {
        const response = await fetch(`https://doticom.vn:3000/api/dinh-muc-nguyen-lieu/${maSP}/${maNL}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          if (selectedProduct && selectedProduct !== 'all') {
            await fetchDinhMucByProduct(selectedProduct);
          } else {
            await fetchDinhMuc();
          }
          toast.success('Đã xóa định mức nguyên liệu thành công', {
            position: "top-right",
            autoClose: 3000,
          });
        } else {
          const errorData = await response.json();
          toast.error(`Không thể xóa định mức: ${errorData.message || 'Lỗi không xác định'}`, {
            position: "top-right",
            autoClose: 5000,
          });
        }
      } catch (error) {
        console.error('Error deleting quota:', error);
        toast.error('Có lỗi xảy ra khi xóa định mức', {
          position: "top-right",
          autoClose: 5000,
        });
      }
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingItem) {
      const updateData = {
        DinhMuc: roundToFiveDecimals(formData.ingredients[0].DinhMuc), // Round before update
        DinhMuc_QuyDoi: roundToFiveDecimals(formData.ingredients[0].DinhMuc_QuyDoi || 0), // Add DinhMuc_QuyDoi
        TenUser: formData.TenUser
      };
      updateDinhMuc(editingItem.MaSP, editingItem.MaNL, updateData);
    } else {
      addDinhMuc(formData);
    }
  };

  // Open modal for add/edit
  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        MaSP: item.MaSP,
        TenUser: item.TenUser || 'admin',
        ingredients: [{ MaNL: item.MaNL, DinhMuc: item.DinhMuc, DinhMuc_QuyDoi: item.DinhMuc_QuyDoi || '' }]
      });
    } else {
      setEditingItem(null);
      setFormData({
        MaSP: selectedProduct && selectedProduct !== 'all' ? selectedProduct : '',
        TenUser: 'admin',
        ingredients: [{ MaNL: '', DinhMuc: '', DinhMuc_QuyDoi: '' }]
      });
    }
    setShowModal(true);
    // Reset copy section
    setShowCopySection(false);
    setSourceProduct('');
    setSourceQuotas([]);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      MaSP: '',
      TenUser: 'admin',
      ingredients: [{ MaNL: '', DinhMuc: '', DinhMuc_QuyDoi: '' }]
    });
    // Reset copy section
    setShowCopySection(false);
    setSourceProduct('');
    setSourceQuotas([]);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle ingredient change
  const handleIngredientChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => 
        i === index ? { ...ing, [field]: value } : ing
      )
    }));
  };

  // Add new ingredient row
  const addIngredientRow = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { MaNL: '', DinhMuc: '', DinhMuc_QuyDoi: '' }]
    }));
  };

  // Remove ingredient row
  const removeIngredientRow = (index) => {
    if (formData.ingredients.length > 1) {
      setFormData(prev => ({
        ...prev,
        ingredients: prev.ingredients.filter((_, i) => i !== index)
      }));
    }
  };

  // Get available ingredients (exclude already used ones)
  const getAvailableIngredients = (currentIndex) => {
    const usedIngredients = formData.ingredients
      .map((ing, idx) => idx !== currentIndex ? ing.MaNL : null)
      .filter(Boolean);
    
    return nguyenLieuList.filter(ingredient => 
      !usedIngredients.includes(ingredient.MaNL)
    );
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchKeyword(e.target.value);
  };

  // Handle product selection
  const handleProductChange = async (e) => {
    const value = e.target.value;
    setSelectedProduct(value);
    
    if (value && value !== 'all') {
      await fetchDinhMucByProduct(value);
    } else {
      await fetchDinhMuc();
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    if (selectedProduct && selectedProduct !== 'all') {
      await fetchDinhMucByProduct(selectedProduct);
    } else {
      await fetchDinhMuc();
    }
  };

  // Group quota data by product
  const getGroupedDinhMuc = () => {
    const filtered = getFilteredDinhMuc();
    const grouped = {};
    
    filtered.forEach(item => {
      if (!grouped[item.MaSP]) {
        grouped[item.MaSP] = {
          MaSP: item.MaSP,
          TenSP: item.TenSP,
          ingredients: []
        };
      }
      grouped[item.MaSP].ingredients.push(item);
    });
    
    return Object.values(grouped);
  };

  // Toggle expand/collapse for a product
  const toggleProductExpansion = (maSP) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(maSP)) {
      newExpanded.delete(maSP);
    } else {
      newExpanded.add(maSP);
    }
    setExpandedProducts(newExpanded);
  };

  // Utility function to check if unit is KG (case insensitive)
  const isKgUnit = (unit) => {
    if (!unit) return false;
    return unit.toLowerCase() === 'kg';
  };

  // Utility function to convert KG to grams
  const convertKgToGrams = (value, unit) => {
    if (isKgUnit(unit) && showInGrams) {
      return roundToFiveDecimals(value * 1000);
    }
    return roundToFiveDecimals(value);
  };

  // Get display unit
  const getDisplayUnit = (unit) => {
    if (isKgUnit(unit) && showInGrams) {
      return 'g';
    }
    return unit || 'đvt';
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Load base data first
        await Promise.all([
          fetchSanPham(),
          fetchNguyenLieu()
        ]);
      } catch (error) {
        console.error('Error loading base data:', error);
      }
    };
    
    loadData();
  }, []);

  // Load quota data after base data is ready
  useEffect(() => {
    if (sanPhamList.length > 0 && nguyenLieuList.length > 0) {
      fetchDinhMuc();
    }
  }, [sanPhamList, nguyenLieuList]);

  // Re-enrich data when base data changes
  useEffect(() => {
    if (dinhMucList.length > 0 && sanPhamList.length > 0 && nguyenLieuList.length > 0) {
      const enrichedData = enrichDinhMucData(dinhMucList);
      setDinhMucList(enrichedData);
    }
  }, [sanPhamList, nguyenLieuList]);

  const filteredDinhMuc = getFilteredDinhMuc();
  const { totalQuotas, totalProducts, filteredQuotas } = getStats();
  const groupedDinhMuc = getGroupedDinhMuc();
  const productsWithQuotas = getProductsWithQuotas();

  return (
    <>
      {/* Header */}
      <div className="admin-header">
        <div className="header-left">
          <h1>Quản lý Định mức Nguyên liệu</h1>
          <p>Quản lý định mức nguyên liệu cho bánh mì</p>
        </div>
        <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Unit Toggle Button */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            background: 'white',
            padding: '8px 12px',
            borderRadius: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e9ecef'
          }}>
            <FaWeight style={{ 
              color: showInGrams ? '#28a745' : '#6c757d', 
              fontSize: '14px' 
            }} />
            <span style={{ 
              fontSize: '13px', 
              fontWeight: '600',
              color: '#495057'
            }}>
              Hiển thị:
            </span>
            <button
              onClick={() => setShowInGrams(!showInGrams)}
              style={{
                background: showInGrams ? '#28a745' : '#6c757d',
                color: 'white',
                border: 'none',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                minWidth: '60px'
              }}
              title={showInGrams ? 'Đang hiển thị KG dưới dạng gram' : 'Nhấn để hiển thị KG dưới dạng gram'}
            >
              {showInGrams ? 'Gram' : 'KG/g'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="content-section">
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '24px' }}>
          <div className="stat-card">
            <div className="stat-header">
              <div className="stat-icon">
                <FaWeight />
              </div>
            </div>
            <div className="stat-content">
              <div className="stat-value">{totalQuotas}</div>
              <div className="stat-label">Tổng định mức</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-header">
              <div className="stat-icon">
                <FaBreadSlice />
              </div>
            </div>
            <div className="stat-content">
              <div className="stat-value">{totalProducts}</div>
              <div className="stat-label">Sản phẩm bánh mì</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-header">
              <div className="stat-icon">
                <FaList />
              </div>
            </div>
            <div className="stat-content">
              <div className="stat-value">{filteredQuotas}</div>
              <div className="stat-label">Đang hiển thị</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="content-section">
        <div className="card">
          <div className="card-header">
            <div className="shift-actions">
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Tìm kiếm định mức..."
                  value={searchKeyword}
                  onChange={handleSearch}
                  className="form-control"
                  style={{ width: '250px', minWidth: '200px' }}
                />
                
                <select
                  value={selectedProduct}
                  onChange={handleProductChange}
                  className="form-select"
                  style={{ width: '300px', minWidth: '200px' }}
                >
                  <option value="">Tất cả sản phẩm</option>
                  <option value="all">Xem tất cả ({totalQuotas})</option>
                  {sanPhamList.map(product => {
                    const hasQuotas = productsWithQuotas.has(product.MaSP);
                    const quotaCount = dinhMucList.filter(dm => dm.MaSP === product.MaSP).length;
                    return (
                      <option key={product.MaSP} value={product.MaSP}>
                        {hasQuotas ? '✓ ' : '○ '}{product.Ten_SP} ({product.MaSP}){hasQuotas ? ` - ${quotaCount} NL` : ''}
                      </option>
                    );
                  })}
                </select>
                
                <button 
                  className="btn btn-primary"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <FaSyncAlt style={{ marginRight: '4px' }} />
                  Làm mới
                </button>

                {/* Unit conversion info */}
                {showInGrams && (
                  <div style={{
                    background: '#e8f5e9',
                    color: '#2e7d32',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: '1px solid #c8e6c9'
                  }}>
                    <FaWeight style={{ fontSize: '10px' }} />
                    KG → Gram
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={importDinhMucFromExcel}
                  style={{ display: 'none' }}
                />
                <button
                  className="btn btn-outline-success"
                  onClick={exportDinhMucToExcel}
                  disabled={isImportingExcel}
                >
                  <FaFileExcel style={{ marginRight: '4px' }} />
                  Xuất Excel
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={triggerImportFile}
                  disabled={isImportingExcel}
                >
                  <FaFileImport style={{ marginRight: '4px' }} />
                  Import Excel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => openModal()}
                  disabled={isImportingExcel}
                >
                  <FaPlus style={{ marginRight: '4px' }} />
                  Thêm định mức
                </button>
              </div>
            </div>
          </div>

          <div className="card-body">
            {selectedProduct && selectedProduct !== 'all' && (
              <div style={{ 
                background: '#f8f9fa', 
                padding: '12px 16px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaBreadSlice className="text-primary" />
                  <strong>Sản phẩm đang xem:</strong>
                  <span className="badge" style={{ 
                    background: '#1976d2', 
                    color: 'white', 
                    padding: '4px 8px', 
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    {sanPhamList.find(p => p.MaSP === selectedProduct)?.Ten_SP || selectedProduct}
                  </span>
                  <span className="text-muted">({filteredDinhMuc.length} định mức)</span>
                </div>
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Đang tải dữ liệu...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table shift-table" style={{ marginBottom: '0' }}>
                  <thead>
                    <tr style={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white'
                    }}>
                      <th style={{ 
                        width: '60px', 
                        textAlign: 'center',
                        padding: '16px 8px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        <FaLayerGroup />
                      </th>
                      <th style={{ 
                        padding: '16px 12px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        <FaBreadSlice style={{ marginRight: '8px' }} />
                        Mã sản phẩm
                      </th>
                      <th style={{ 
                        padding: '16px 12px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        <FaUtensils style={{ marginRight: '8px' }} />
                        Tên sản phẩm
                      </th>
                      <th style={{ 
                        padding: '16px 12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        textAlign: 'center',
                        width: '150px'
                      }}>
                        <FaWeight style={{ marginRight: '8px' }} />
                        Nguyên liệu
                      </th>
                      <th style={{ 
                        padding: '16px 12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        textAlign: 'center',
                        width: '120px'
                      }}>
                        Thao tác
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedDinhMuc.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ 
                          textAlign: 'center', 
                          padding: '60px 20px',
                          backgroundColor: '#f8f9fa'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: '16px' 
                          }}>
                            <FaBoxOpen style={{ 
                              fontSize: '64px', 
                              color: '#dee2e6',
                              marginBottom: '8px'
                            }} />
                            <div>
                              <h5 style={{ color: '#6c757d', marginBottom: '8px' }}>
                                {searchKeyword 
                                  ? 'Không tìm thấy định mức nào' 
                                  : selectedProduct && selectedProduct !== 'all'
                                    ? 'Sản phẩm này chưa có định mức nguyên liệu'
                                    : 'Chưa có định mức nguyên liệu nào'
                              }
                              </h5>
                              <p className="text-muted" style={{ fontSize: '14px', margin: '0' }}>
                                {!searchKeyword && 'Nhấn "Thêm định mức" để bắt đầu tạo định mức cho sản phẩm'}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      groupedDinhMuc.map((group, groupIndex) => (
                        <React.Fragment key={group.MaSP}>
                          {/* Product Header Row */}
                          <tr 
                            style={{ 
                              background: expandedProducts.has(group.MaSP) 
                                ? 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)'
                                : 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                              cursor: 'pointer',
                              borderLeft: `5px solid ${expandedProducts.has(group.MaSP) ? '#2196f3' : '#dee2e6'}`,
                              transition: 'all 0.3s ease',
                              boxShadow: expandedProducts.has(group.MaSP) 
                                ? '0 2px 8px rgba(33, 150, 243, 0.15)'
                                : '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                            onClick={() => toggleProductExpansion(group.MaSP)}
                            onMouseEnter={(e) => {
                              if (!expandedProducts.has(group.MaSP)) {
                                e.target.closest('tr').style.background = 'linear-gradient(135deg, #f0f8ff 0%, #f9f9f9 100%)';
                                e.target.closest('tr').style.borderLeft = '5px solid #90caf9';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!expandedProducts.has(group.MaSP)) {
                                e.target.closest('tr').style.background = 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)';
                                e.target.closest('tr').style.borderLeft = '5px solid #dee2e6';
                              }
                            }}
                          >
                            <td style={{ 
                              textAlign: 'center',
                              padding: '20px 8px',
                              verticalAlign: 'middle'
                            }}>
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                {expandedProducts.has(group.MaSP) ? (
                                  <FaChevronDown style={{ 
                                    color: '#2196f3', 
                                    fontSize: '16px',
                                    transition: 'transform 0.3s ease',
                                    transform: 'rotate(0deg)'
                                  }} />
                                ) : (
                                  <FaChevronRight style={{ 
                                    color: '#9e9e9e', 
                                    fontSize: '16px',
                                    transition: 'transform 0.3s ease'
                                  }} />
                                )}
                                <div style={{
                                  width: '24px',
                                  height: '3px',
                                  background: expandedProducts.has(group.MaSP) 
                                    ? 'linear-gradient(90deg, #2196f3, #21cbf3)'
                                    : '#dee2e6',
                                  borderRadius: '2px',
                                  transition: 'all 0.3s ease'
                                }} />
                              </div>
                            </td>
                            <td style={{ 
                              padding: '20px 16px',
                              verticalAlign: 'middle'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '10px',
                                  background: 'linear-gradient(135deg, #ff9800, #f57c00)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 3px 6px rgba(255, 152, 0, 0.3)'
                                }}>
                                  <FaBreadSlice style={{ color: 'white', fontSize: '18px' }} />
                                </div>
                                <div>
                                  <div style={{ 
                                    fontWeight: 'bold', 
                                    color: '#1976d2',
                                    fontSize: '15px',
                                    marginBottom: '2px'
                                  }}>
                                    {group.MaSP}
                                  </div>
                                  <div style={{ 
                                    fontSize: '12px', 
                                    color: '#666',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>
                                    Mã sản phẩm
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td style={{ 
                              padding: '20px 16px',
                              verticalAlign: 'middle'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div>
                                  <div style={{
                                    fontWeight: 'bold',
                                    fontSize: '16px',
                                    color: '#2c3e50',
                                    marginBottom: '4px'
                                  }}>
                                    {group.TenSP || 'Chưa có tên'}
                                  </div>
                                  <div 
                                  >
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td style={{ 
                              padding: '20px 16px',
                              textAlign: 'center',
                              verticalAlign: 'middle'
                            }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                <div style={{ 
                                  background: 'linear-gradient(135deg, #4caf50, #388e3c)', 
                                  color: 'white', 
                                  padding: '8px 16px', 
                                  borderRadius: '20px',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  boxShadow: '0 3px 6px rgba(76, 175, 80, 0.3)',
                                  minWidth: '80px'
                                }}>
                                  {group.ingredients.length}
                                </div>
                                <div style={{ 
                                  fontSize: '11px', 
                                  color: '#666',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  Nguyên liệu
                                </div>
                              </div>
                            </td>
                            <td style={{ 
                              padding: '20px 16px',
                              textAlign: 'center',
                              verticalAlign: 'middle'
                            }}>
                              <button
                                className="btn btn-primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openModal();
                                  setFormData(prev => ({
                                    ...prev,
                                    MaSP: group.MaSP
                                  }));
                                }}
                                style={{
                                  padding: '8px 16px',
                                  borderRadius: '20px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  background: 'linear-gradient(135deg, #2196f3, #1976d2)',
                                  border: 'none',
                                  boxShadow: '0 3px 6px rgba(33, 150, 243, 0.3)',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 5px 12px rgba(33, 150, 243, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = '0 3px 6px rgba(33, 150, 243, 0.3)';
                                }}
                                title="Thêm nguyên liệu cho sản phẩm này"
                              >
                                <FaPlus style={{ marginRight: '6px', fontSize: '12px' }} />
                                Thêm NL
                              </button>
                            </td>
                          </tr>
                          
                          {/* Ingredient Detail Rows (Expandable) */}
                          {expandedProducts.has(group.MaSP) && (
                            <>
                              {/* Ingredient Header */}
                              <tr style={{ 
                                background: 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)',
                                borderLeft: '5px solid #2196f3'
                              }}>
                                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                  <FaList style={{ color: '#666', fontSize: '14px' }} />
                                </td>
                                <td colSpan="4" style={{ padding: '12px 16px' }}>
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '12px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#495057'
                                  }}>
                                    <FaUtensils style={{ color: '#4caf50' }} />
                                    <span>Chi tiết nguyên liệu ({group.ingredients.length} loại)</span>
                                    <div style={{ flex: 1, height: '1px', background: '#dee2e6' }} />
                                  </div>
                                </td>
                              </tr>
                              
                              {group.ingredients.map((item, index) => (
                                <tr 
                                  key={`${item.MaSP}-${item.MaNL}-${index}`}
                                  style={{ 
                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                    borderLeft: '5px solid #e1f5fe',
                                    transition: 'all 0.3s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.closest('tr').style.backgroundColor = '#e3f2fd';
                                    e.target.closest('tr').style.borderLeft = '5px solid #2196f3';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.closest('tr').style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
                                    e.target.closest('tr').style.borderLeft = '5px solid #e1f5fe';
                                  }}
                                >
                                  <td style={{ 
                                    textAlign: 'center',
                                    padding: '16px 8px',
                                    verticalAlign: 'middle'
                                  }}>
                                    <div style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '8px',
                                      background: 'linear-gradient(135deg, #4caf50, #388e3c)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      margin: '0 auto',
                                      boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)'
                                    }}>
                                      <FaUtensils style={{ color: 'white', fontSize: '14px' }} />
                                    </div>
                                  </td>
                                  <td style={{ 
                                    padding: '16px 20px',
                                    verticalAlign: 'middle'
                                  }}>
                                    <div>
                                      <div style={{
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        color: '#2c3e50',
                                        marginBottom: '4px'
                                      }}>
                                        {item.MaNL}
                                      </div>
                                      <div style={{
                                        fontSize: '11px',
                                        color: '#666',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                      }}>
                                        Mã nguyên liệu
                                      </div>
                                    </div>
                                  </td>
                                  <td style={{ 
                                    padding: '16px 20px',
                                    verticalAlign: 'middle'
                                  }}>
                                    <div>
                                      <div style={{
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        color: '#2c3e50',
                                        marginBottom: '6px'
                                      }}>
                                        {item.TenNL || 'Chưa có tên'}
                                      </div>
                                      <div style={{ 
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        background: 'rgba(96, 125, 139, 0.1)',
                                        padding: '4px 12px',
                                        borderRadius: '12px',
                                        fontSize: '12px',
                                        color: '#607d8b',
                                        fontWeight: '600'
                                      }}>
                                        <FaWeight style={{ fontSize: '10px' }} />
                                        {item.DVT || 'Chưa có đvt'}
                                      </div>
                                    </div>
                                  </td>
                                  <td style={{
                                    padding: '16px 20px',
                                    textAlign: 'center',
                                    verticalAlign: 'middle'
                                  }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                      <div style={{
                                        fontSize: '20px',
                                        fontWeight: 'bold',
                                        color: isKgUnit(item.DVT) && showInGrams ? '#28a745' : '#1976d2',
                                        lineHeight: '1'
                                      }}>
                                        {convertKgToGrams(item.DinhMuc, item.DVT).toFixed(5)}
                                      </div>
                                      <div style={{
                                        fontSize: '11px',
                                        color: '#666',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}>
                                        {getDisplayUnit(item.DVT)}
                                        {isKgUnit(item.DVT) && showInGrams && (
                                          <span style={{
                                            background: '#28a745',
                                            color: 'white',
                                            padding: '2px 4px',
                                            borderRadius: '4px',
                                            fontSize: '9px',
                                            fontWeight: 'bold'
                                          }}>
                                            ×1000
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td style={{
                                    padding: '16px 20px',
                                    textAlign: 'center',
                                    verticalAlign: 'middle'
                                  }}>
                                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                      <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={() => openModal(item)}
                                        style={{
                                          padding: '8px 12px',
                                          borderRadius: '8px',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          border: '2px solid #2196f3',
                                          transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.background = '#2196f3';
                                          e.target.style.color = 'white';
                                          e.target.style.transform = 'translateY(-1px)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.background = 'transparent';
                                          e.target.style.color = '#2196f3';
                                          e.target.style.transform = 'translateY(0)';
                                        }}
                                        title="Chỉnh sửa định mức"
                                      >
                                        <FaEdit />
                                      </button>
                                      <button
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => deleteDinhMuc(item.MaSP, item.MaNL)}
                                        style={{
                                          padding: '8px 12px',
                                          borderRadius: '8px',
                                          fontSize: '12px',
                                          fontWeight: '600',
                                          border: '2px solid #f44336',
                                          transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.background = '#f44336';
                                          e.target.style.color = 'white';
                                          e.target.style.transform = 'translateY(-1px)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.background = 'transparent';
                                          e.target.style.color = '#f44336';
                                          e.target.style.transform = 'translateY(0)';
                                        }}
                                        title="Xóa định mức"
                                      >
                                        <FaTrash />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '1000px', width: '95%' }}>
            <div className="modal-header">
              <div>
                <h3>{editingItem ? 'Cập nhật định mức' : 'Thêm định mức mới'}</h3>
                <p className="text-muted" style={{ margin: '4px 0 0 0', fontSize: '14px' }}>
                  {editingItem ? 'Chỉnh sửa thông tin định mức nguyên liệu' : 'Chọn sản phẩm bánh mì và thêm định mức cho các nguyên liệu'}
                </p>
              </div>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Product Selection */}
                <div className="form-group" style={{ marginBottom: '24px' }}>
                  <label htmlFor="MaSP" style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: '#2c3e50', 
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <FaBreadSlice style={{ color: '#e67e22' }} />
                    Sản phẩm bánh mì *
                  </label>
                  <select
                    id="MaSP"
                    name="MaSP"
                    value={formData.MaSP}
                    onChange={handleInputChange}
                    className="form-select"
                    required
                    disabled={editingItem}
                    style={{ 
                      fontSize: '15px', 
                      padding: '12px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px'
                    }}
                  >
                    <option value="">-- Chọn sản phẩm bánh mì --</option>
                    {sanPhamList.map(product => (
                      <option key={product.MaSP} value={product.MaSP}>
                        {product.Ten_SP} ({product.MaSP})
                      </option>
                    ))}
                  </select>
                  <small className="text-muted" style={{ marginTop: '6px', display: 'block', fontSize: '13px' }}>
                    <FaBreadSlice style={{ marginRight: '4px', fontSize: '12px' }} />
                    Chỉ hiển thị sản phẩm thuộc danh mục "Bánh mì các loại" (BMCN)
                  </small>
                </div>

                {/* Selected Product Info */}
                {formData.MaSP && (
                  <div style={{ 
                    background: 'linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)', 
                    padding: '16px', 
                    borderRadius: '12px', 
                    marginBottom: '24px',
                    border: '1px solid #2196f3'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FaBreadSlice style={{ color: '#1976d2', fontSize: '18px' }} />
                        <strong style={{ color: '#1976d2', fontSize: '16px' }}>Sản phẩm được chọn:</strong>
                        <span style={{ 
                          background: '#1976d2', 
                          color: 'white', 
                          padding: '6px 12px', 
                          borderRadius: '20px',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>
                          {sanPhamList.find(p => p.MaSP === formData.MaSP)?.Ten_SP || formData.MaSP}
                        </span>
                      </div>

                      {/* Quick Copy Button - Only show in add mode and if there are products with quotas */}
                      {!editingItem && getProductsWithQuotasForCopy().length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowCopySection(!showCopySection)}
                          className={`btn btn-sm ${showCopySection ? 'btn-warning' : 'btn-outline-success'}`}
                          style={{
                            borderRadius: '20px',
                            padding: '8px 16px',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <FaCopy style={{ marginRight: '6px', fontSize: '12px' }} />
                          {showCopySection ? 'Đóng sao chép' : 'Sao chép từ SP khác'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Copy from existing product section */}
                {!editingItem && showCopySection && (
                  <div style={{
                    background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
                    border: '2px solid #ffc107',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      <FaCopy style={{ color: '#856404', fontSize: '20px' }} />
                      <h5 style={{ color: '#856404', margin: 0, fontWeight: 'bold' }}>
                        Sao chép định mức từ sản phẩm khác
                      </h5>
                    </div>

                    <p style={{ color: '#856404', margin: '0 0 16px 0', fontSize: '14px' }}>
                      Chọn sản phẩm có sẵn định mức để sao chép và chỉnh sửa cho sản phẩm hiện tại
                    </p>

                    <div className="row align-items-end">
                      <div className="col-md-8">
                        <label style={{ 
                          fontSize: '14px', 
                          fontWeight: 'bold', 
                          color: '#856404',
                          marginBottom: '8px',
                          display: 'block'
                        }}>
                          Chọn sản phẩm nguồn
                        </label>
                        <select
                          value={sourceProduct}
                          onChange={handleSourceProductChange}
                          className="form-select"
                          disabled={loadingSourceQuotas}
                          style={{
                            fontSize: '14px',
                            padding: '10px',
                            border: '2px solid #ffc107',
                            borderRadius: '8px'
                          }}
                        >
                          <option value="">-- Chọn sản phẩm để sao chép định mức --</option>
                          {getProductsWithQuotasForCopy().map(product => {
                            const quotaCount = dinhMucList.filter(dm => dm.MaSP === product.MaSP).length;
                            return (
                              <option key={product.MaSP} value={product.MaSP}>
                                {product.Ten_SP} ({product.MaSP}) - {quotaCount} định mức
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      <div className="col-md-4">
                        <button
                          type="button"
                          onClick={copyQuotasFromSource}
                          disabled={!sourceProduct || sourceQuotas.length === 0 || loadingSourceQuotas}
                          className="btn btn-warning w-100"
                          style={{
                            borderRadius: '20px',
                            padding: '10px',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}
                        >
                          {loadingSourceQuotas ? (
                            <>
                              <div className="spinner-border spinner-border-sm me-2" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              Đang tải...
                            </>
                          ) : (
                            <>
                              <FaArrowRight style={{ marginRight: '8px' }} />
                              Sao chép {sourceQuotas.length} định mức
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Display source quotas preview */}
                    {sourceQuotas.length > 0 && (
                      <div style={{
                        marginTop: '16px',
                        padding: '12px',
                        background: 'rgba(255,255,255,0.7)',
                        borderRadius: '8px',
                        border: '1px solid #ffc107'
                      }}>
                        <small style={{ color: '#856404', fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                          Xem trước định mức sẽ được sao chép:
                        </small>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {sourceQuotas.map((quota, index) => (
                            <span
                              key={index}
                              style={{
                                background: '#856404',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}
                            >
                              {quota.TenNL || quota.MaNL}: {roundToFiveDecimals(quota.DinhMuc)} {quota.DVT}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {editingItem ? (
                  // Edit mode - single ingredient
                  <div style={{ 
                    border: '2px solid #e9ecef', 
                    borderRadius: '12px', 
                    padding: '20px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <h5 style={{ 
                      color: '#495057', 
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FaUtensils style={{ color: '#28a745' }} />
                      Thông tin nguyên liệu
                    </h5>
                    
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="edit-MaNL" style={{ fontWeight: 'bold', color: '#495057' }}>
                            Nguyên liệu bánh mì *
                          </label>
                          <select
                            id="edit-MaNL"
                            value={formData.ingredients[0].MaNL}
                            onChange={(e) => handleIngredientChange(0, 'MaNL', e.target.value)}
                            className="form-select"
                            required
                            disabled={true}
                            style={{ fontSize: '14px', padding: '10px' }}
                          >
                            <option value="">Chọn nguyên liệu</option>
                            {nguyenLieuList.map(ingredient => (
                              <option key={ingredient.MaNL} value={ingredient.MaNL}>
                                {ingredient.TenNL} - {ingredient.DVT}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label htmlFor="edit-DinhMuc" style={{ fontWeight: 'bold', color: '#495057' }}>
                            Định mức *
                          </label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number"
                              id="edit-DinhMuc"
                              value={formData.ingredients[0].DinhMuc}
                              onChange={(e) => handleIngredientChange(0, 'DinhMuc', e.target.value)}
                              className="form-control"
                              required
                              min="0"
                              step="0.00001"
                              placeholder="0.00000"
                              style={{ 
                                fontSize: '14px', 
                                padding: '10px',
                                paddingRight: '50px',
                                textAlign: 'right',
                                fontWeight: 'bold'
                              }}
                            />
                            <span style={{
                              position: 'absolute',
                              right: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6c757d',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {getDisplayUnit(nguyenLieuList.find(ing => ing.MaNL === formData.ingredients[0].MaNL)?.DVT)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="form-group">
                          <label htmlFor="edit-DinhMuc_QuyDoi" style={{ fontWeight: 'bold', color: '#495057' }}>
                            Định mức quy đổi
                          </label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number"
                              id="edit-DinhMuc_QuyDoi"
                              value={formData.ingredients[0].DinhMuc_QuyDoi}
                              onChange={(e) => handleIngredientChange(0, 'DinhMuc_QuyDoi', e.target.value)}
                              className="form-control"
                              min="0"
                              step="0.00001"
                              placeholder="0.00000"
                              style={{ 
                                fontSize: '14px', 
                                padding: '10px',
                                paddingRight: '50px',
                                textAlign: 'right',
                                fontWeight: 'bold'
                              }}
                            />
                            <span style={{
                              position: 'absolute',
                              right: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6c757d',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {getDisplayUnit(nguyenLieuList.find(ing => ing.MaNL === formData.ingredients[0].MaNL)?.DVT)}
                            </span>
                          </div>
                          <small className="text-muted" style={{ marginTop: '4px', display: 'block' }}>
                            Định mức quy đổi (tùy chọn)
                          </small>
                        </div>
                      </div>
                    </div>
                    <small className="text-muted" style={{ marginTop: '12px', display: 'block' }}>
                      Cho 1 đơn vị sản phẩm
                      {isKgUnit(nguyenLieuList.find(ing => ing.MaNL === formData.ingredients[0].MaNL)?.DVT) && showInGrams && (
                        <span style={{ color: '#28a745', fontWeight: 'bold', marginLeft: '8px' }}>
                          (Hiển thị dưới dạng gram)
                        </span>
                      )}
                    </small>
                  </div>
                ) : (
                  // Add mode - multiple ingredients
                  <div className="form-group">
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: '16px' 
                    }}>
                      <label style={{ 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: '#2c3e50',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        margin: 0
                      }}>
                        <FaUtensils style={{ color: '#28a745' }} />
                        Danh sách nguyên liệu *
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span className="badge" style={{ 
                          background: '#28a745', 
                          color: 'white', 
                          fontSize: '12px',
                          padding: '6px 12px',
                          borderRadius: '20px'
                        }}>
                          {nguyenLieuList.length} nguyên liệu có sẵn
                        </span>
                        <span className="badge" style={{ 
                          background: '#17a2b8', 
                          color: 'white', 
                          fontSize: '12px',
                          padding: '6px 12px',
                          borderRadius: '20px'
                        }}>
                          {formData.ingredients.filter(ing => ing.MaNL && ing.DinhMuc).length} đã chọn
                        </span>
                      </div>
                    </div>
                    
                    <div style={{ 
                      border: '2px solid #e9ecef', 
                      borderRadius: '12px', 
                      padding: '20px',
                      backgroundColor: '#f8f9fa',
                      maxHeight: '500px',
                      overflowY: 'auto'
                    }}>
                      {formData.ingredients.map((ingredient, index) => {
                        const selectedIngredient = nguyenLieuList.find(ing => ing.MaNL === ingredient.MaNL);
                        return (
                          <div key={index} style={{ 
                            background: 'white',
                            border: '1px solid #dee2e6',
                            borderRadius: '10px',
                            padding: '20px',
                            marginBottom: index < formData.ingredients.length - 1 ? '16px' : '0',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.3s ease'
                          }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              marginBottom: '16px'
                            }}>
                              <h6 style={{ 
                                margin: 0,
                                color: '#495057',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <FaUtensils style={{ color: '#28a745', fontSize: '14px' }} />
                                Nguyên liệu #{index + 1}
                              </h6>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                {formData.ingredients.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeIngredientRow(index)}
                                    className="btn btn-sm btn-outline-danger"
                                    style={{ 
                                      borderRadius: '20px',
                                      padding: '4px 8px',
                                      fontSize: '12px'
                                    }}
                                    title="Xóa nguyên liệu này"
                                  >
                                    <FaTrash style={{ marginRight: '4px' }} />
                                    Xóa
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-6">
                                <label style={{ 
                                  fontSize: '13px', 
                                  fontWeight: 'bold', 
                                  color: '#495057',
                                  marginBottom: '6px',
                                  display: 'block'
                                }}>
                                  Chọn nguyên liệu *
                                </label>
                                <select
                                  value={ingredient.MaNL}
                                  onChange={(e) => handleIngredientChange(index, 'MaNL', e.target.value)}
                                  className="form-select"
                                  required
                                  style={{ 
                                    fontSize: '14px',
                                    padding: '10px',
                                    border: ingredient.MaNL ? '2px solid #28a745' : '2px solid #dee2e6'
                                  }}
                                >
                                  <option value="">-- Chọn nguyên liệu bánh mì --</option>
                                  {getAvailableIngredients(index).map(ing => (
                                    <option key={ing.MaNL} value={ing.MaNL}>
                                      {ing.TenNL} ({ing.MaNL})
                                    </option>
                                  ))}
                                </select>
                              </div>
                              
                              <div className="col-md-2">
                                <label style={{ 
                                  fontSize: '13px', 
                                  fontWeight: 'bold', 
                                  color: '#495057',
                                  marginBottom: '6px',
                                  display: 'block'
                                }}>
                                  Định mức *
                                </label>
                                <div style={{ position: 'relative' }}>
                                  <input
                                    type="number"
                                    value={ingredient.DinhMuc}
                                    onChange={(e) => handleIngredientChange(index, 'DinhMuc', e.target.value)}
                                    className="form-control"
                                    placeholder="0.00000"
                                    min="0"
                                    step="0.00001"
                                    required
                                    style={{ 
                                      fontSize: '14px',
                                      padding: '10px',
                                      paddingRight: '50px',
                                      textAlign: 'right',
                                      fontWeight: 'bold',
                                      border: ingredient.DinhMuc ? '2px solid #007bff' : '2px solid #dee2e6'
                                    }}
                                  />
                                  <span style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#6c757d',
                                    fontSize: '12px',
                                    fontWeight: 'bold'
                                  }}>
                                    {getDisplayUnit(selectedIngredient?.DVT)}
                                  </span>
                                </div>
                              </div>

                              <div className="col-md-2">
                                <label style={{ 
                                  fontSize: '13px', 
                                  fontWeight: 'bold', 
                                  color: '#495057',
                                  marginBottom: '6px',
                                  display: 'block'
                                }}>
                                  Định mức QĐ
                                </label>
                                <div style={{ position: 'relative' }}>
                                  <input
                                    type="number"
                                    value={ingredient.DinhMuc_QuyDoi}
                                    onChange={(e) => handleIngredientChange(index, 'DinhMuc_QuyDoi', e.target.value)}
                                    className="form-control"
                                    placeholder="0.00000"
                                    min="0"
                                    step="0.00001"
                                    style={{ 
                                      fontSize: '14px',
                                      padding: '10px',
                                      paddingRight: '50px',
                                      textAlign: 'right',
                                      fontWeight: 'bold',
                                      border: ingredient.DinhMuc_QuyDoi ? '2px solid #17a2b8' : '2px solid #dee2e6'
                                    }}
                                  />
                                  <span style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#6c757d',
                                    fontSize: '12px',
                                    fontWeight: 'bold'
                                  }}>
                                    {getDisplayUnit(selectedIngredient?.DVT)}
                                  </span>
                                </div>
                                <small className="text-muted" style={{ fontSize: '11px', marginTop: '2px', display: 'block' }}>
                                  Tùy chọn
                                </small>
                              </div>

                              <div className="col-md-2" style={{ display: 'flex', alignItems: 'end' }}>
                                {index === formData.ingredients.length - 1 && 
                                 getAvailableIngredients(index + 1).length > 0 && (
                                  <button
                                    type="button"
                                    onClick={addIngredientRow}
                                    className="btn btn-success btn-sm w-100"
                                    style={{ 
                                      borderRadius: '20px',
                                      padding: '8px',
                                      fontSize: '12px'
                                    }}
                                    title="Thêm nguyên liệu khác"
                                  >
                                    <FaPlus style={{ marginRight: '4px' }} />
                                    Thêm
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Display ingredient info when selected */}
                            {selectedIngredient && (
                              <div style={{
                                marginTop: '12px',
                                padding: '10px',
                                background: '#e8f5e8',
                                borderRadius: '6px',
                                border: '1px solid #c3e6cb'
                              }}>
                                <small style={{ 
                                  color: '#155724',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '12px'
                                }}>
                                  <FaUtensils />
                                  <strong>{selectedIngredient.TenNL}</strong>
                                  <span>|</span>
                                  <span>Đơn vị: <strong>{getDisplayUnit(selectedIngredient.DVT)}</strong></span>
                                  <span>|</span>
                                  <span>Mã: <strong>{selectedIngredient.MaNL}</strong></span>
                                  {isKgUnit(selectedIngredient.DVT) && showInGrams && (
                                    <>
                                      <span>|</span>
                                      <span style={{ color: '#28a745', fontWeight: 'bold' }}>
                                        <FaWeight style={{ marginRight: '2px', fontSize: '10px' }} />
                                        Chế độ Gram (×1000)
                                      </span>
                                    </>
                                  )}
                                </small>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {formData.ingredients.length < nguyenLieuList.length && 
                       getAvailableIngredients(formData.ingredients.length).length > 0 && (
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                          <button
                            type="button"
                            onClick={addIngredientRow}
                            className="btn btn-outline-success"
                            style={{ 
                              padding: '12px 24px',
                              borderRadius: '25px',
                              border: '2px dashed #28a745'
                            }}
                          >
                            <FaPlus style={{ marginRight: '8px' }} />
                            Thêm nguyên liệu khác 
                            <span className="badge bg-success ms-2">
                              {getAvailableIngredients(formData.ingredients.length).length} còn lại
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <small className="text-muted" style={{ marginTop: '12px', display: 'block', fontSize: '13px' }}>
                      <FaWeight style={{ marginRight: '4px' }} />
                      Chọn nguyên liệu bánh mì từ danh sách và nhập định mức tương ứng. 
                      Định mức là số lượng nguyên liệu cần thiết để sản xuất 1 đơn vị sản phẩm.
                      {showInGrams && (
                        <span style={{ color: '#28a745', fontWeight: 'bold', marginLeft: '8px' }}>
                          <FaWeight style={{ marginRight: '4px', fontSize: '12px' }} />
                          (Các nguyên liệu có đơn vị KG sẽ hiển thị dưới dạng gram)
                        </span>
                      )}
                    </small>
                  </div>
                )}

                {/* User field */}
                <div className="form-group" style={{ marginTop: '24px' }}>
                  <label htmlFor="TenUser" style={{ 
                    fontSize: '14px', 
                    fontWeight: 'bold', 
                    color: '#495057',
                    marginBottom: '6px',
                    display: 'block'
                  }}>
                    Người tạo
                  </label>
                  <input
                    type="text"
                    id="TenUser"
                    name="TenUser"
                    value={formData.TenUser}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Tên người tạo định mức"
                    style={{ 
                      fontSize: '14px', 
                      padding: '10px',
                      border: '2px solid #e9ecef',
                      borderRadius: '8px'
                    }}
                  />
                </div>
              </div>

              <div className="modal-footer" style={{ padding: '20px', borderTop: '2px solid #e9ecef' }}>
                <button type="button" className="btn btn-secondary" onClick={closeModal} style={{ borderRadius: '20px', padding: '10px 20px' }}>
                  <FaTimes style={{ marginRight: '6px' }} />
                  Hủy bỏ
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={!formData.MaSP || 
                           formData.ingredients.filter(ing => ing.MaNL && ing.DinhMuc).length === 0}
                  style={{ 
                    borderRadius: '20px', 
                    padding: '10px 20px',
                    background: editingItem ? '#17a2b8' : '#007bff'
                  }}
                >
                  <FaSave style={{ marginRight: '6px' }} />
                  {editingItem 
                    ? 'Cập nhật định mức' 
                    : `Lưu ${formData.ingredients.filter(ing => ing.MaNL && ing.DinhMuc).length} định mức`
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isImportingExcel && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            backdropFilter: 'blur(2px)'
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '24px 32px',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.16)',
              textAlign: 'center',
              minWidth: '280px'
            }}
          >
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="sr-only">Importing...</span>
            </div>
            <div style={{ marginTop: '16px', fontWeight: '700', color: '#2c3e50' }}>
              Đang import dữ liệu Excel
            </div>
            <div style={{ marginTop: '8px', fontSize: '14px', color: '#6c757d' }}>
              Vui lòng chờ trong khi hệ thống cập nhật định mức từ file.
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default QuanLyDinhMuc;
